import { useRouter } from 'expo-router';
import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { Text } from '@/components/ui/Text';
import { TextField } from '@/components/ui/TextField';
import { createTemplate } from '@/lib/data';
import { useAsyncAction } from '@/lib/useAsyncAction';

export interface NewTemplateSheetProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Controlled "New checklist" sheet — names a template, creates it, and opens its
 * editor. Shared by the Home and Templates screens so both create flows match.
 */
export function NewTemplateSheet({ visible, onClose }: NewTemplateSheetProps) {
  const router = useRouter();
  const [title, setTitle] = useState('');
  const { busy, error, setError, run } = useAsyncAction();

  // Reset on close so the next open starts from a clean slate.
  function close() {
    setTitle('');
    setError(null);
    onClose();
  }

  async function submit() {
    const name = title.trim();
    if (!name) return;
    await run(async () => {
      const templateId = await createTemplate({ title: name });
      close();
      router.push(`/template/${templateId}`);
    });
  }

  return (
    <Sheet visible={visible} onClose={close} title="New template">
      <Text variant="caption" className="mb-3">
        A template is a reusable checklist. Name it now, then add your steps. You can
        rename it or add a description anytime.
      </Text>
      <TextField
        value={title}
        onChangeText={setTitle}
        placeholder="Name this template"
        autoFocus
        returnKeyType="done"
        onSubmitEditing={submit}
        error={error ?? undefined}
      />
      <Button
        className="mt-3"
        block
        label="Create template"
        busy={busy}
        disabled={!title.trim()}
        onPress={submit}
      />
    </Sheet>
  );
}
